import { IntermediaryApplicationProps } from 'api/IntermediaryApplicationsApi';

import {
  CONTRACTOR_SAME_ADDRESSES_MAP,
  DEPRECATED_FEDERAL_TAX_IDS,
  FEDERAL_TAX_ID_WITH_DASH_MAX_LENGTH,
  MAX_INTERMEDIARY_OWNERS,
  SOMEONE_ELSE_OPTION,
} from './сontractorApplication.constants';
import { FormDataContractorApplication, OwnerInformation } from './contractorApplications.types';
import { HoldLicense, InstallProductsYouSell, IntermediaryOwnerVariable } from './contractorApplication.enums';

export const einMask = (taxId: string) => {
  let formatNumber = taxId?.replace(/\D/g, '');

  return formatNumber.length > 2 ? `${formatNumber.slice(0, 2)}-${formatNumber.slice(2, 9)}` : formatNumber;
};

export const setDefaultValues = (
  contractorApplication: Partial<IntermediaryApplicationProps>,
): FormDataContractorApplication => {
  return {
    intermediary_business_name: contractorApplication.intermediary_business_name || '',
    intermediary_corporate_entity_name: contractorApplication.intermediary_corporate_entity_name || '',
    intermediary_federal_tax_id: einMask(contractorApplication.intermediary_federal_tax_id || ''),
    intermediary_owner_first_name: '',
    intermediary_owner_last_name: '',
    intermediary_main_contact_first_name: contractorApplication.intermediary_main_contact_first_name || '',
    intermediary_main_contact_last_name: contractorApplication.intermediary_main_contact_last_name || '',
    intermediary_phone: contractorApplication.intermediary_phone || '',
    intermediary_website_or_facebook_page: contractorApplication.intermediary_website_or_facebook_page || '',
    intermediary_how_did_you_hear_about_us: contractorApplication.intermediary_how_did_you_hear_about_us || '',
    intermediary_email: contractorApplication.intermediary_email || '',
    confirm_intermediary_email: contractorApplication.intermediary_email || '',
    intermediary_corporate_zip_code:
      contractorApplication.intermediary_corporate_address_information?.zipOrPostalCode || '',
    intermediary_corporate_address: `${
      contractorApplication.intermediary_corporate_address_information?.streetNumber || ''
    } ${contractorApplication.intermediary_corporate_address_information?.streetName || ''}`.trim(),
    intermediary_corporate_city: contractorApplication.intermediary_corporate_address_information?.city || '',
    intermediary_corporate_state:
      contractorApplication.intermediary_corporate_address_information?.stateOrProvince || '',
    intermediary_years_in_business: contractorApplication.intermediary_years_in_business?.toString() || '',
    intermediary_hold_appropriate_license: contractorApplication.intermediary_hold_appropriate_license
      ? HoldLicense.Yes
      : contractorApplication.intermediary_hold_appropriate_license === false
      ? HoldLicense.No
      : null,
    intermediary_hold_appropriate_license_description:
      contractorApplication.intermediary_hold_appropriate_license_description || '',
    intermediary_city: contractorApplication.intermediary_mailing_address_information?.city || '',
    intermediary_state: contractorApplication.intermediary_mailing_address_information?.stateOrProvince || '',
    intermediary_zip_code: contractorApplication.intermediary_mailing_address_information?.zipOrPostalCode || '',
    intermediary_mailing_address: `${
      contractorApplication.intermediary_mailing_address_information?.streetNumber || ''
    } ${contractorApplication.intermediary_mailing_address_information?.streetName || ''}`.trim(),
    intermediary_other_home_improvement_type: contractorApplication.intermediary_other_home_improvement_type || '',
    intermediary_annual_sales: (contractorApplication.intermediary_annual_sales || '').toString(),
    intermediary_average_jobs: (contractorApplication.intermediary_average_jobs || '').toString(),
    same_address: false,
    intermediary_business_entity_structure: contractorApplication.intermediary_business_entity_structure || null,
    intermediary_year_of_incorporation: contractorApplication.intermediary_year_of_incorporation || '',
    intermediary_states_of_operation: contractorApplication.intermediary_states_of_operation || [],
    intermediary_do_you_install_the_products_you_sell:
      contractorApplication.intermediary_do_you_install_the_products_you_sell
        ? InstallProductsYouSell.Yes
        : contractorApplication.intermediary_do_you_install_the_products_you_sell === false
        ? InstallProductsYouSell.No
        : null,
    intermediary_installer_count: (contractorApplication.intermediary_installer_count || '').toString(),
    intermediary_associated_manufacturer_1: contractorApplication.intermediary_associated_manufacturer_1 || '',
    intermediary_associated_manufacturer_2: contractorApplication.intermediary_associated_manufacturer_2 || '',
    intermediary_percent_of_sales_financed: (
      (contractorApplication.intermediary_percent_of_sales_financed || 0) * 100 || ''
    ).toString(),
    intermediary_main_contact_phone: contractorApplication.intermediary_main_contact_phone || '',
    intermediary_main_contact_email: contractorApplication.intermediary_main_contact_email || '',
    intermediary_main_contact_position: contractorApplication.intermediary_main_contact_position || null,
    intermediary_primary_home_improvement_category:
      contractorApplication.intermediary_primary_home_improvement_category || null,
    intermediary_other_home_improvement_category:
      contractorApplication.intermediary_other_home_improvement_category || [],
    intermediary_total_owners: contractorApplication.intermediary_owners?.length.toString() || '',
    intermediary_owner_first_name_1: contractorApplication.intermediary_owners?.[0]?.firstName || '',
    intermediary_owner_last_name_1: contractorApplication.intermediary_owners?.[0]?.lastName || '',
    intermediary_owner_percentage_owned_1: (
      (contractorApplication.intermediary_owners?.[0]?.percentageOwned || 0) * 100 || ''
    ).toString(),
    intermediary_owner_cell_phone_1: contractorApplication.intermediary_owners?.[0]?.phoneNumber || '',
    intermediary_owner_first_name_2: contractorApplication.intermediary_owners?.[1]?.firstName || '',
    intermediary_owner_last_name_2: contractorApplication.intermediary_owners?.[1]?.lastName || '',
    intermediary_owner_percentage_owned_2: (
      (contractorApplication.intermediary_owners?.[1]?.percentageOwned || 0) * 100 || ''
    ).toString(),
    intermediary_owner_cell_phone_2: contractorApplication.intermediary_owners?.[1]?.phoneNumber || '',
    intermediary_owner_first_name_3: contractorApplication.intermediary_owners?.[2]?.firstName || '',
    intermediary_owner_last_name_3: contractorApplication.intermediary_owners?.[2]?.lastName || '',
    intermediary_owner_percentage_owned_3: (
      (contractorApplication.intermediary_owners?.[2]?.percentageOwned || 0) * 100 || ''
    ).toString(),
    intermediary_owner_cell_phone_3: contractorApplication.intermediary_owners?.[2]?.phoneNumber || '',
    intermediary_owner_first_name_4: contractorApplication.intermediary_owners?.[3]?.firstName || '',
    intermediary_owner_last_name_4: contractorApplication.intermediary_owners?.[3]?.lastName || '',
    intermediary_owner_percentage_owned_4: (
      (contractorApplication.intermediary_owners?.[3]?.percentageOwned || 0) * 100 || ''
    ).toString(),
    intermediary_owner_cell_phone_4: contractorApplication.intermediary_owners?.[3]?.phoneNumber || '',
    totalPercentageOwned: 0,
    intermediary_who_signed_master_dealer_agreement:
      contractorApplication.intermediary_who_signed_master_dealer_agreement || '',
    intermediary_authorized_signer_first_name: contractorApplication.intermediary_authorized_signer_first_name || '',
    intermediary_authorized_signer_last_name: contractorApplication.intermediary_authorized_signer_last_name || '',
    intermediary_authorized_signer_position: contractorApplication.intermediary_authorized_signer_position || '',
  };
};

export const convertTotalOwnersValueToNumber = (totalOwners: string): number => {
  return !!totalOwners ? +totalOwners : 0;
};

export const convertPercentageValue = (percent: string) => {
  if (!percent) {
    return '';
  }

  let formatNumber = percent?.replace(/[^0-9.]/g, '');

  return formatNumber;
};

export const getSameAddressVariable = (addressVariable: string) => {
  for (const [key, value] of Object.entries(CONTRACTOR_SAME_ADDRESSES_MAP)) {
    if (key === addressVariable) {
      return value;
    }

    if (value === addressVariable) {
      return key;
    }
  }

  return null;
};

export const validateTaxId = (value: string) =>
  (value.length === FEDERAL_TAX_ID_WITH_DASH_MAX_LENGTH && !DEPRECATED_FEDERAL_TAX_IDS.includes(value)) ||
  'Please enter a valid Tax Id';

export const getVariableKey = (variable: string, index: number | string): string => {
  return `${variable}_${index}`;
};

export const getVariableDisplayName = (displayName: string, index: string | number): string => {
  return `Owner ${index} ${displayName}`;
};

export const prepareTotalOwnersArray = (data: FormDataContractorApplication): OwnerInformation[] => {
  const result: OwnerInformation[] = [];

  for (let index = 1; index <= convertTotalOwnersValueToNumber(data.intermediary_total_owners); index++) {
    result.push({
      firstName: data[getVariableKey(IntermediaryOwnerVariable.FirstName, index)],
      lastName: data[getVariableKey(IntermediaryOwnerVariable.LastName, index)],
      percentageOwned: +data[getVariableKey(IntermediaryOwnerVariable.PercentageOwned, index)] / 100,
      phoneNumber: data[getVariableKey(IntermediaryOwnerVariable.CellPhone, index)],
    });
  }

  return result;
};

export const calculateTotalPercentageOwned = (data: Partial<FormDataContractorApplication>): number => {
  let totalPercentageOwned: number = 0;

  for (let index = 1; index <= MAX_INTERMEDIARY_OWNERS; index++) {
    const percentageOwned = +data[getVariableKey(IntermediaryOwnerVariable.PercentageOwned, index)];

    if (!isNaN(percentageOwned)) {
      totalPercentageOwned += percentageOwned;
    }
  }

  return totalPercentageOwned;
};

export const getUniqueMasterDialerOptions = (
  ownersArray: OwnerInformation[],
  mainContactFirstName: string,
  mainContactLastName: string,
) => {
  const options = ownersArray.map((owner) => {
    return {
      value: `${owner.firstName} ${owner.lastName}`,
      label: `${owner.firstName} ${owner.lastName}`,
    };
  });

  options.push({
    value: `${mainContactFirstName} ${mainContactLastName}`,
    label: `${mainContactFirstName} ${mainContactLastName}`,
  });

  options.push({
    value: SOMEONE_ELSE_OPTION,
    label: SOMEONE_ELSE_OPTION,
  });

  const seen = new Set();

  return options.filter((item) => {
    if (!seen.has(item.value)) {
      seen.add(item.value);

      return true;
    }

    return false;
  });
};
